import { defineStore } from 'pinia'
import userAuthApi from 'modules/authentication/api/userAuthApi'
import validateAuthApi from 'modules/authentication/api/ValidateAuthApi'
import refreshAuthApi from 'modules/authentication/api/refreshAuthApi'
import validateRequest from 'modules/authentication/models/validateRequest'
import { handlingApiError } from 'modules/shared/utilities/HandlingError'

function decodeJwtPayload(jwt) {
  if (!jwt || typeof jwt !== 'string') return null
  const raw = jwt.startsWith('Bearer ') ? jwt.slice(7) : jwt
  const parts = raw.split('.')
  if (parts.length < 2) return null
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4 !== 0) base64 += '='
  try {
    return JSON.parse(atob(base64))
  } catch (_) {
    return null
  }
}

function normalizeBearer(token) {
  if (!token || typeof token !== 'string') return ''
  return token.startsWith('Bearer ') ? token : `Bearer ${token}`
}

function getLang() {
  try {
    return localStorage.getItem('i18nextLng') || navigator.language || 'es-AR'
  } catch (_) {
    return 'es-AR'
  }
}

function safeJsonParse(value, fallback = null) {
  try {
    return JSON.parse(value)
  } catch (_) {
    return fallback
  }
}

function pickAccessToken(payload) {
  return (
    payload?.bearer ||
    payload?.accessToken ||
    payload?.token ||
    payload?.oidc?.access_token ||
    payload?.oidc?.accessToken ||
    payload?.data?.bearer ||
    payload?.data?.accessToken ||
    payload?.data?.token ||
    payload?.data?.oidc?.access_token ||
    payload?.data?.oidc?.accessToken ||
    null
  )
}

function pickSession(payload) {
  return (
    payload?.session ||
    payload?.data?.session ||
    null
  )
}

function pickUserData(payload) {
  return (
    payload?.user ||
    payload?.reference ||
    payload?.introspect?.data ||
    payload?.introspect ||
    payload?.data?.user ||
    payload?.data ||
    null
  )
}

export const useAuthLoginStore = defineStore('useAuthLoginStore', {
  state: () => ({
    idToken: null,
    refreshToken: null,         // ✅ agregado
    status: 'authenticating',
    authSession: { id: '', member: '' },
    userId: null,
    userReference: null,
    userInfo: null,
    errorMessage: null,
    isAdmin: localStorage.getItem('isAdmin') === 'true',
    adminUserId: '019d6413-125c-7bf2-b008-f7494b957ce3',
    userCompanyInfo: null,
    companyId: null,
    agencyId: null,
    authorities: null,
    exp: null,
    iat: null,
    refreshInFlight: false,
    lastSilentRefreshAt: 0,
    showRefreshModal: false,
  }),

  getters: {
    authIdToken: (state) => state.idToken,
    currentStatus: (state) => state.status,
    userLoggedReference: (state) => state.userReference,
    userLoggedInfo: (state) => state.userInfo,
    apiErrorMessage: (state) => state.errorMessage,
    isUserAdmin: (state) => state.isAdmin,
    companyIdValue: (state) => state.companyId,
    agencyIdValue: (state) => state.agencyId,
    userIdValue: (state) => state.userId,
    authoritiesValue: (state) => state.authorities,
    expValue: (state) => state.exp,
    iatValue: (state) => state.iat,
  },

  actions: {
    authenticateUser(idToken) {
      let token = typeof idToken === 'string' ? idToken : idToken?.bearer || ''
      token = normalizeBearer(token)
      if (token) {
        localStorage.setItem('idToken', token)
        this.idToken = token
      }
      this.status = 'authenticated'
    },

    getMsUntilExpiry() {
      if (!this.exp) return 0
      return (this.exp * 1000) - Date.now()
    },

    setAuthSession(session) {
      this.authSession = {
        id: session?.id || '',
        member: session?.member || ''
      }
      localStorage.setItem('authSession', JSON.stringify(this.authSession))
    },

    rehydrateAuthSession() {
      let id = this.authSession?.id || ''
      let member = this.authSession?.member || ''
      if (id && member) return { id, member }

      const raw = localStorage.getItem('authSession')
      if (!raw) return { id: '', member: '' }

      const parsed = safeJsonParse(raw, null)
      if (!parsed?.id || !parsed?.member) return { id: '', member: '' }

      this.authSession = { id: parsed.id, member: parsed.member }
      return this.authSession
    },

    setIsUserAdmin(status) {
      localStorage.setItem('isAdmin', String(status))
      this.isAdmin = status
    },

    setCompanyId(id) { this.companyId = id || null },
    setAgencyId(id) { this.agencyId = id || null },
    setUserId(id) { this.userId = id || null },
    setAuthorities(value) { this.authorities = value || null },
    setExp(value) { this.exp = value ?? null },
    setIat(value) { this.iat = value ?? null },
    setUserReference(reference) { this.userReference = reference || null },

    setAuthUserInfo(dataAuth) {
      if (!dataAuth) return

      const jwtPayload = decodeJwtPayload(this.idToken)

      const name =
        dataAuth?.name ?? dataAuth?.given_name ?? dataAuth?.preferred_username ??
        jwtPayload?.name ?? jwtPayload?.preferred_username ?? ''

      const surname =
        dataAuth?.surname ?? dataAuth?.family_name ?? jwtPayload?.family_name ?? ''

      const userId =
        dataAuth?.userId ?? dataAuth?.id ?? dataAuth?.sub ?? jwtPayload?.sub ?? null

      const companyId =
        dataAuth?.companyId ??
        dataAuth?.company?.id ??
        jwtPayload?.companyId ??
        jwtPayload?.reference?.companyId ??
        null

      const agencyId =
        dataAuth?.agencyId ??
        dataAuth?.agency?.id ??
        jwtPayload?.agencyId ??
        jwtPayload?.reference?.agencyId ??
        null

      const authorities =
        dataAuth?.authorities ?? dataAuth?.permissions ?? jwtPayload?.authorities ?? null

      const exp = dataAuth?.exp ?? jwtPayload?.exp ?? null
      const iat = dataAuth?.iat ?? jwtPayload?.iat ?? null

      this.userInfo = {
        name,
        surname,
        email: dataAuth?.email ?? jwtPayload?.email ?? null
      }

      if (userId !== null) this.setUserId(userId)
      if (companyId !== null) this.setCompanyId(companyId)
      if (agencyId !== null) this.setAgencyId(agencyId)
      const effectiveUserId = userId ?? this.userId
      if (effectiveUserId !== null) {
        this.setIsUserAdmin(this.adminUserId === effectiveUserId)
      }
      if (authorities !== null) this.setAuthorities(authorities)
      if (exp !== null) this.setExp(exp)
      if (iat !== null) this.setIat(iat)
    },

    logout() {
      this.idToken = null
      this.refreshToken = null    // ✅ agregado
      this.userReference = null
      this.userInfo = null
      this.errorMessage = null
      this.status = 'not-authenticated'
      this.authSession = { id: '', member: '' }
      this.userId = null
      this.userCompanyInfo = null
      this.companyId = null
      this.agencyId = null
      this.authorities = null
      this.exp = null
      this.iat = null
      this.isAdmin = false

      localStorage.removeItem('idToken')
      localStorage.removeItem('refreshToken')   // ✅ agregado
      localStorage.removeItem('authSession')
      localStorage.removeItem('isAdmin')
    },

    async signInUser(userData) {
      try {
        const lang = getLang()

        const { data: dataResponse } = await userAuthApi.post('', userData, {
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'Accept': 'application/json; charset=UTF-8',
            'Accept-Language': lang,
            'Api-Key': process.env.API_GATEWAY_API_KEY
          }
        })

        const success =
          dataResponse?.success === true || dataResponse?.ok === true

        const payload = dataResponse?.data ?? dataResponse

        const session = pickSession(payload)
        if (session?.id && session?.member) this.setAuthSession(session)

        const accessToken = pickAccessToken(payload)
        if (accessToken) this.authenticateUser(accessToken)

        // ✅ guardar refresh_token
        const refreshToken = payload?.oidc?.refresh_token || null
        if (refreshToken) {
          this.refreshToken = refreshToken
          localStorage.setItem('refreshToken', refreshToken)
        }

        // ✅ decodear id_token para poblar userInfo, exp, companyId, agencyId
        const idToken = payload?.oidc?.id_token
        const tokenToDecode = idToken || accessToken

        if (tokenToDecode) {
          const decoded = decodeJwtPayload(tokenToDecode)
          if (decoded) {
            const userDataFromJwt = {
              ...decoded,
              ...(decoded.reference || {})
            }
            this.setAuthUserInfo(userDataFromJwt)
          }
        }

        if (payload?.reference) this.setUserReference(payload.reference)

        return { success, data: payload }
      } catch (error) {
        return handlingApiError(error)
      }
    },

    async validateAuthentication() {
      try {
        const dataBody = JSON.parse(JSON.stringify(validateRequest))
        if (!dataBody.authIntrospect) dataBody.authIntrospect = {}

        const session = this.rehydrateAuthSession()
        const id = session?.id || ''
        const member = session?.member || ''

        if (!id || !member) {
          return { success: false, data: null, error: 'missingSession' }
        }

        // ✅ fallback del JWT si el store no tiene los ids
        let companyId = this.companyId
        let agencyId = this.agencyId
        let userId = this.userId

        if (!companyId || !agencyId) {
          const decoded = decodeJwtPayload(localStorage.getItem('idToken'))
          const ref = decoded?.reference || {}
          companyId = companyId || ref.companyId || ''
          agencyId = agencyId || ref.agencyId || ''
          userId = userId || ref.userId || decoded?.sub || ''
        }

        dataBody.authIntrospect.authReference = {
          environmentId: process.env.ENVIROMENT_ID || '',
          userId,
          subscriptionId: '',
          companyId,
          agencyId
        }

        dataBody.authIntrospect.authSession = { id, member }

        const { data: dataResponse } = await validateAuthApi.post('', dataBody, {
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'Accept': 'application/json; charset=UTF-8',
            'Accept-Language': getLang(),
            'Session': id,
            'Member': member
          }
        })

        const success =
          dataResponse?.success === true || dataResponse?.ok === true

        const data = dataResponse?.data ?? dataResponse

        return { success, data }
      } catch (error) {
        return handlingApiError(error)
      }
    },

    async checkAuthentication() {
      this.idToken = localStorage.getItem('idToken')

      if (!this.idToken) {
        this.logout()
        return { ok: false, message: 'tokenMissing' }
      }

      const { data, success } = await this.validateAuthentication()

      if (!success) {
        this.logout()
        return { ok: false, message: 'tokenInvalid' }
      }

      this.authenticateUser(this.idToken)
      this.setAuthUserInfo(data)

      // Completar desde el JWT solo los campos de identidad que el validate puede no traer.
      // No llamar setAuthUserInfo de nuevo porque sobreescribiría userInfo.name con vacío
      // si el JWT no tiene name/given_name/preferred_username.
      const decoded = decodeJwtPayload(this.idToken)
      if (decoded) {
        const ref = decoded.reference || {}
        const companyId = decoded.companyId ?? ref.companyId ?? null
        const agencyId  = decoded.agencyId  ?? ref.agencyId  ?? null
        const userId    = decoded.userId ?? decoded.sub ?? ref.userId ?? null
        const exp       = decoded.exp ?? null
        const iat       = decoded.iat ?? null

        if (companyId !== null) this.setCompanyId(companyId)
        if (agencyId  !== null) this.setAgencyId(agencyId)
        if (userId    !== null) {
          this.setUserId(userId)
          this.setIsUserAdmin(this.adminUserId === userId)
        }
        if (exp !== null) this.setExp(exp)
        if (iat !== null) this.setIat(iat)
      }

      return { ok: true, message: '' }
    },

    async refreshSession() {
      try {
        const session = this.rehydrateAuthSession()
        const id = session?.id || ''
        const member = session?.member || ''

        if (!id || !member) return { success: false }

        const refreshToken = this.refreshToken || localStorage.getItem('refreshToken') || ''

        // ✅ siempre leer del JWT — es la fuente de verdad
        const decoded = decodeJwtPayload(localStorage.getItem('idToken'))
        const ref = decoded?.reference || {}

        const environmentId = ref.environmentId || process.env.ENVIROMENT_ID || ''
        const userId = ref.userId || ''
        const companyId = ref.companyId || ''
        const agencyId = ref.agencyId || ''
        const subscriptionId = ref.subscriptionId || ''

        const payload = {
          authRefresh: {
            authReference: {
              environmentId,
              userId,
              subscriptionId,
              companyId,
              agencyId
            },
            authSession: { id, member },
            authCallback: {
              url: 'https://quantidia.com',
              userId: 'userId',
              password: 'password',
              auth: 'auth',
              retries: '0'
            },
            authNotify: {
              email: 'no-reply@quantidia.com',
              sms: '',
              push: ''
            }
          }
        }

        const { data: dataResponse } = await refreshAuthApi.post('', payload, {
          headers: {
            'Content-Type': 'application/json; charset=UTF-8',
            'Accept': 'application/json; charset=UTF-8',
            'Accept-Language': getLang(),
            'Authorization': `Bearer ${refreshToken}`,
            'Session': id,
            'Member': member
          }
        })

        const success =
          dataResponse?.success === true || dataResponse?.ok === true

        const data = dataResponse?.data ?? dataResponse

        const accessToken = pickAccessToken(data)
        if (accessToken) this.authenticateUser(accessToken)

        const newRefreshToken = data?.oidc?.refresh_token || null
        if (newRefreshToken) {
          this.refreshToken = newRefreshToken
          localStorage.setItem('refreshToken', newRefreshToken)
        }


        const newIdToken = data?.oidc?.id_token || null
const tokenForExpiry = newIdToken || accessToken

if (tokenForExpiry) {
  const decoded = decodeJwtPayload(tokenForExpiry)
  if (decoded?.exp) this.setExp(decoded.exp)
  if (decoded?.iat) this.setIat(decoded.iat)

  // ✅ repoblar companyId, agencyId, userId desde el nuevo JWT
  if (decoded) {
    const userDataFromJwt = {
      ...decoded,
      ...(decoded.reference || {})
    }
    this.setAuthUserInfo(userDataFromJwt)
  }
}

        const newSession = pickSession(data)
        if (newSession?.id && newSession?.member) this.setAuthSession(newSession)

        return { success }
      } catch (error) {
        return handlingApiError(error)
      }
    },

    async maybeRefreshSilently(hasRecentActivity) {
      if (this.refreshInFlight) return { success: false, skipped: 'inFlight' }
      if (!this.exp) return { success: false, skipped: 'noExp' }

      const msUntilExpiry = (this.exp * 1000) - Date.now()
      const twoMinutes = 2 * 60 * 1000

      if (msUntilExpiry > twoMinutes || msUntilExpiry <= 0) {
        return { success: false, skipped: 'notInWindow' }
      }

      if (!hasRecentActivity) {
        return { success: false, skipped: 'noRecentActivity' }
      }

      // antes: < 30000
      if (Date.now() - this.lastSilentRefreshAt < 25_000) {
        return { success: false, skipped: 'throttled' }
      }

      this.refreshInFlight = true

      try {
        const result = await this.refreshSession()
        if (result?.success) {
          this.lastSilentRefreshAt = Date.now()
          this.showRefreshModal = false
        }
        return result
      } finally {
        this.refreshInFlight = false
      }
    }
  },
})